import { Component } from '@theme/component';

/**
 * A horizontally scrolling strip of shoppable photos that never dead-ends.
 *
 * Why three copies of the track rather than two: a native scroll container clamps scrollLeft at
 * 0, so a strip that duplicates its content only once can wrap forward but hits a hard wall
 * going backward. Rendering the set three times and parking the viewport on the middle copy
 * leaves a full copy of runway in each direction. Every copy is identical, so snapping
 * scrollLeft by exactly one copy-width is invisible.
 *
 * Native overflow-x already provides touch swipe, trackpad, and shift+wheel scrolling. The only
 * things added here are mouse drag (with click suppression, so a drag that ends on a photo never
 * opens the product), the wrap itself, and optional autoplay.
 *
 * Autoplay is a slow constant drift driven by requestAnimationFrame rather than a timer that
 * snaps to the next slide, so it reads as a lifestyle strip drifting by rather than a slideshow
 * ticking over. It pauses for any reason a person is actively engaging with the strip (mouse
 * hover, keyboard focus inside it, a drag or arrow-triggered scroll in flight) or can't see or
 * shouldn't be shown motion (tab hidden, scrolled off screen, prefers-reduced-motion), and only
 * resumes once every one of those reasons has cleared.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The scrolling element.
 * @property {HTMLElement} track - The flex row inside the viewport.
 * @property {HTMLButtonElement} [previous] - Previous arrow, when arrows are enabled.
 * @property {HTMLButtonElement} [next] - Next arrow, when arrows are enabled.
 *
 * @extends Component<Refs>
 */
class ShoppableGalleryCarousel extends Component {
  requiredRefs = ['viewport', 'track'];

  /** Width of a single copy of the photo set, in pixels. */
  #copyWidth = 0;

  /** True once the track has been tripled and the viewport parked in the middle. */
  #looping = false;

  /** Set while an arrow-driven smooth scroll is in flight, to defer wrapping until it settles. */
  #animating = false;

  #dragged = false;

  /** Pixels per second of automatic drift. 0 (the default, no `autoplay` attribute) disables it. */
  #speed = 0;

  /**
   * Reasons autoplay is currently held. Tracked as a set rather than a boolean because the
   * reasons overlap: the pointer can leave the strip while the tab is still hidden, and whichever
   * one clears last must not restart drift the others still want stopped.
   */
  #holds = new Set();

  #frameHandle = 0;

  #lastFrameTime = 0;

  /** Float mirror of scrollLeft, so sub-pixel drift accumulates instead of rounding away. */
  #position = 0;

  /** scrollLeft right after the last autoplay write, to tell our own scrolling from everyone else's. */
  #expected = -1;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    const { viewport } = this.refs;

    this.#speed = Math.max(0, parseFloat(this.getAttribute('autoplay') ?? '') || 0);
    this.#setHold('hidden', document.hidden);
    this.#setHold('reduced-motion', this.#reducedMotion.matches);

    this.#setup();

    viewport.addEventListener('scroll', this.#handleScroll, { passive: true });
    viewport.addEventListener('pointerdown', this.#handlePointerDown);
    viewport.addEventListener('click', this.#handleClick, { capture: true });
    window.addEventListener('resize', this.#handleResize);

    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.addEventListener('change', this.#handleReducedMotionChange);

    // A strip scrolled out of view has nothing to show; drifting it just burns frames.
    this.#observer = new IntersectionObserver(([entry]) => this.#setHold('offscreen', !entry.isIntersecting));
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { viewport } = this.refs;

    viewport.removeEventListener('scroll', this.#handleScroll);
    viewport.removeEventListener('pointerdown', this.#handlePointerDown);
    viewport.removeEventListener('click', this.#handleClick, { capture: true });
    window.removeEventListener('resize', this.#handleResize);

    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.removeEventListener('change', this.#handleReducedMotionChange);

    this.#observer?.disconnect();
    this.#stopAutoplay();
  }

  /** Advance one photo. Bound declaratively by snippets/slideshow-arrow.liquid. */
  next() {
    this.#scrollByOneSlide(1);
  }

  /** Go back one photo. Bound declaratively by snippets/slideshow-arrow.liquid. */
  previous() {
    this.#scrollByOneSlide(-1);
  }

  /**
   * Triples the track and parks the viewport on the middle copy. Skipped when the photos already
   * fit without overflowing, since there is nothing to loop through in that case.
   */
  #setup() {
    const { viewport, track } = this.refs;

    // Start from a clean slate so this can re-run on resize or a theme editor re-render.
    for (const clone of track.querySelectorAll('[data-gallery-clone]')) clone.remove();

    this.#looping = false;
    this.#copyWidth = track.scrollWidth;

    // Autoplay rides on the loop: without one it would drift into the end of the strip and stick.
    if (this.getAttribute('loop') !== 'true') return this.#syncAutoplay();
    if (this.#copyWidth <= viewport.clientWidth + 1) return this.#syncAutoplay();

    const originals = Array.from(track.children);

    for (let copy = 0; copy < 2; copy++) {
      for (const original of originals) {
        track.appendChild(this.#cloneSlide(original));
      }
    }

    this.#looping = true;
    viewport.scrollLeft = this.#copyWidth;

    this.#syncAutoplay();
  }

  /**
   * Clones one slide for the loop. Clones are hidden from assistive tech and taken out of the tab
   * order so each product is announced and tabbed to exactly once, and the theme editor's block
   * attributes are stripped so it never tries to select a clone.
   *
   * @param {Element} original
   * @returns {Element}
   */
  #cloneSlide(original) {
    const clone = /** @type {Element} */ (original.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-gallery-clone', '');
    clone.removeAttribute('id');

    for (const link of clone.querySelectorAll('a')) link.setAttribute('tabindex', '-1');
    for (const node of [clone, ...clone.querySelectorAll('[data-shopify-editor-block]')]) {
      node.removeAttribute('data-shopify-editor-block');
    }

    return clone;
  }

  /**
   * Keeps the viewport inside the middle copy. Because every copy is identical, shifting by one
   * copy-width leaves the photos exactly where they appear to be.
   */
  #handleScroll = () => {
    if (!this.#looping || this.#animating) return;

    const { viewport } = this.refs;
    const copy = this.#copyWidth;
    const position = viewport.scrollLeft;

    if (position < copy * 0.5) {
      viewport.scrollLeft = position + copy;
    } else if (position > copy * 1.5) {
      viewport.scrollLeft = position - copy;
    }
  };

  /** @param {number} direction */
  #scrollByOneSlide(direction) {
    const { viewport, track } = this.refs;
    const slide = track.firstElementChild;
    if (!slide) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = slide.getBoundingClientRect().width + gap;

    // Defer wrapping until the smooth scroll settles, otherwise adjusting scrollLeft mid-flight
    // fights the animation.
    this.#animating = true;
    this.#setHold('arrow', true);
    viewport.scrollBy({ left: distance * direction, behavior: 'smooth' });

    window.clearTimeout(this.#animationTimeout);
    this.#animationTimeout = window.setTimeout(() => {
      this.#animating = false;
      this.#handleScroll();
      this.#setHold('arrow', false);
    }, 400);
  }

  #animationTimeout = 0;

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#setHold('pointer', true);

    // Touch flings keep coasting after the finger lifts, and writing scrollLeft mid-fling kills
    // that momentum, so wait a beat before drift resumes.
    const releaseDelay = event.pointerType === 'mouse' ? 0 : 700;
    const release = () => window.setTimeout(() => this.#setHold('pointer', false), releaseDelay);

    document.addEventListener('pointerup', release, { once: true });
    document.addEventListener('pointercancel', release, { once: true });

    // Touch and pen already scroll natively with momentum; only mouse needs help.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const { viewport } = this.refs;
    const startX = event.clientX;
    const startScroll = viewport.scrollLeft;
    const controller = new AbortController();
    const { signal } = controller;

    this.#dragged = false;

    /** @param {PointerEvent} moveEvent */
    const onPointerMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;

      if (!this.#dragged && Math.abs(delta) < 4) return;

      if (!this.#dragged) {
        this.#dragged = true;
        this.setAttribute('dragging', '');
      }

      viewport.scrollLeft = startScroll - delta;
    };

    const onPointerUp = () => {
      controller.abort();
      this.removeAttribute('dragging');
      // Leave #dragged set so the click handler can swallow the click this drag would fire.
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  /**
   * Swallows the click that follows a drag, so releasing the mouse over a photo never navigates
   * to that product.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!this.#dragged) return;

    event.preventDefault();
    event.stopPropagation();
    this.#dragged = false;
  };

  #handleResize = () => {
    const { viewport } = this.refs;
    const previous = this.#copyWidth;
    const offset = this.#looping ? viewport.scrollLeft - previous : viewport.scrollLeft;

    this.#setup();

    if (this.#looping) viewport.scrollLeft = this.#copyWidth + offset;
  };

  /**
   * Adds or clears one reason to hold autoplay, then restarts or stops the loop to match.
   *
   * @param {string} reason
   * @param {boolean} held
   */
  #setHold(reason, held) {
    if (held) this.#holds.add(reason);
    else this.#holds.delete(reason);

    this.#syncAutoplay();
  }

  #syncAutoplay() {
    if (this.#speed > 0 && this.#looping && this.#holds.size === 0) this.#startAutoplay();
    else this.#stopAutoplay();
  }

  #startAutoplay() {
    if (this.#frameHandle) return;

    this.#position = this.refs.viewport.scrollLeft;
    this.#expected = this.#position;
    this.#lastFrameTime = 0;
    this.#frameHandle = requestAnimationFrame(this.#frame);
  }

  #stopAutoplay() {
    if (!this.#frameHandle) return;

    cancelAnimationFrame(this.#frameHandle);
    this.#frameHandle = 0;
  }

  /** @param {number} now */
  #frame = (now) => {
    const { viewport } = this.refs;

    this.#frameHandle = requestAnimationFrame(this.#frame);

    // The first frame after a resume only starts the clock; measuring against a stale timestamp
    // would jump the strip forward.
    if (!this.#lastFrameTime) {
      this.#lastFrameTime = now;
      return;
    }

    // Clamp the step so a long task (tab throttling, a slow paint) can't resume with one big leap.
    const elapsed = Math.min(now - this.#lastFrameTime, 100) / 1000;
    this.#lastFrameTime = now;

    // Anything that moved the viewport since our last write (the loop wrap, a trackpad flick)
    // wins: pick the drift back up from where it actually is rather than fighting it.
    if (Math.abs(viewport.scrollLeft - this.#expected) > 1) this.#position = viewport.scrollLeft;

    this.#position += this.#speed * elapsed;
    viewport.scrollLeft = this.#position;
    this.#expected = viewport.scrollLeft;
  };

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    // Touch and pen fire enter on tap without always firing a matching leave, which would strand
    // the hold on. Only a mouse hovers.
    if (event.pointerType !== 'mouse') return;

    this.#setHold('hover', true);
  };

  #handlePointerLeave = () => {
    this.#setHold('hover', false);
  };

  #handleFocusIn = () => {
    this.#setHold('focus', true);
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    // Moving between links inside the strip is still focus inside the strip.
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.#setHold('focus', false);
  };

  #handleVisibilityChange = () => {
    this.#setHold('hidden', document.hidden);
  };

  /** @param {MediaQueryListEvent} event */
  #handleReducedMotionChange = (event) => {
    this.#setHold('reduced-motion', event.matches);
  };
}

if (!customElements.get('shoppable-gallery-carousel')) {
  customElements.define('shoppable-gallery-carousel', ShoppableGalleryCarousel);
}
